import React from 'react';
import styled from 'styled-components';
import { media } from 'theme/breakpoints';
import quote from 'img/ui/quote.svg';
import BlogArticle from 'components/blog/blogArticle';
import { authorKalina as author } from 'data/authors';
import img from 'img/blog/CRO-trends-to-watch.png';
import img1 from 'img/blog/cro-trends-2020/crotrends2020.png';
import img2 from 'img/blog/cro-trends-2020/img2.png';
import img3 from 'img/blog/cro-trends-2020/img3.png';
import img4 from 'img/blog/cro-trends-2020/img4.png';

const Container = styled.div`
  h3,
  h2 {
    font-weight: 700;
    letter-spacing: 0.01em;
    margin: 2em 0 0.8em;
    line-height: 2rem;
  }
  h3 {
    font-size: 1.1rem;
  }
  h2 {
    font-size: 1.5rem;
  }
  h4 {
    color: #777;
    font-weight: 700;
    font-size: 1rem;
  }
  a {
    display: inline;
  }
  .quote {
    margin: 3.5rem 0 2rem;
    position: relative;
    background-color: #f1f5f9;
    padding: 48px;
    p:last-child {
      margin-bottom: 0;
    }
    ${media.mobile} {
      padding: 42px 32px;
    }
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      transform: translate(-30%, -50%);
      background-image: url(${quote});
      background-size: contain;
      background-position: center;
      background-repeat: no-repeat;
      width: 82px;
      height: 61px;
      ${media.mobile} {
        width: 74px;
        height: 74px;
        transform: translate(0, -50%);
      }
    }
  }
  .action-banner {
    background-color: #7cffef;
    padding: 16px 24px;
    font-size: 18px;
    text-decoration: underline;
    color: #000;
    width: 100%;
    display: block;
    margin: 1.5rem 0;
    font-weight: 700;
    border-radius: 4px;
  }
`;

const Content = () => {
  return (
    <Container>
      <p>
        Conversion rate optimization is ever-changing. What is one day considered a brilliant hack,
        may quickly appear as outdated and not effective anymore.
      </p>
      <p>
        This is why we’ve decided to look into current conversion rate optimization trends. What is
        the state of CRO in 2019? And what’s in store for the years to come?
      </p>
      <p>
        We’ve asked a couple of professionals from the industry about their predictions for CRO
        trends in 2020. Here’s what they’ve told us:
      </p>
      <h2>CRO Trend no.1 – Qualitative Data meets Quantitative Data:</h2>
      <p>
        The CRO field is usually associated with numbers. Even the name{' '}
        <i>conversion rate optimization</i> refers to a key metric! A/B tests, the most popular
        experiment type amongst conversion optimization professionals, are based on measuring bounce
        rates, click-through rates and other numeric performance indicators.
      </p>
      <p>
        Does it mean that conversion rate optimization has to be limited to quantitative data? Not
        at all! Experts from the industry agree that adding qualitative data to the mix will help
        you get the most out of your CRO efforts:
      </p>
      <p className="quote">
        <i>
          The rise of quantitative big data has been a trend across all sectors of business, and the
          CRO niche is no different. However, conversion rate and UX professionals will lean heavily
          on qualitative data (think individual case study, descriptive/conceptual data) in terms of
          session replay and user experience tools to help make their products the most intuitive
          and user-friendly. The blend of using{' '}
          <a
            href="https://learn.g2.com/qualitative-vs-quantitative-data/"
            target="_blank"
            rel="noopener noreferrer"
          >
            qualitative and quantitative data
          </a>{' '}
          equally will be a trend specific to the UX/CRO industry.
        </i>
        <br />
        <br />
        <strong>Levi Olmstead</strong> – Manager, SEO and Community Outreach @{' '}
        <a href="https://www.g2.com/" target="_blank" rel="noopener noreferrer">
          G2
        </a>{' '}
      </p>

      <p>
        <strong>How to take action:</strong> The answer is simple – start tracking user sessions!
        With tools like{' '}
        <a href="https://livesession.io/" target="_blank" rel="noopener noreferrer">
          LiveSession
        </a>{' '}
        you can see exactly how the visitors are using your website.
      </p>
      <img
        src={img1}
        alt="Session recording tool"
        className="img-fluid justify-content-center mb-5 mt-3"
        loading="lazy"
        title="Record sessions in LiveSession"
      />
      <p>
        {' '}
        It’s a great opportunity to spot behavioral patterns, pain points and areas for improvement.
        To increase conversion, you need to know what needs to be changed. User sessions take away
        the guesswork, as you can see exactly where the users are struggling. You can use the
        findings to come up with fresh ideas for landing page optimization.
      </p>
      <p>
        Want to learn more and take your website conversion optimization skills to the next level?
        We think you’re going to enjoy our extensive guide to{' '}
        <a href="/blog/guide-to-qualitative-ux-research/" rel="noopener noreferrer" target="_blank">
          qualitative research
        </a>
        .
      </p>
      <a href="/signup/" target="_blank" rel="noopener noreferrer" className="action-banner">
        Get started with qualitative research. Try LiveSession for free!
      </a>
      <h2>CRO Trend no. 2 – Page Speed Matters:</h2>
      <p>
        Did you know that more than half of website visitors are likely to leave if the page is
        loading for{' '}
        <a
          href="https://raventools.com/blog/page-speed-and-why-it-matters/"
          target="_blank"
          rel="noopener noreferrer"
        >
          more than 3 seconds
        </a>
        ?
      </p>

      <p>
        That’s just the tip of the iceberg. Page loading speed triggers a chain reaction: long
        waiting times{' '}
        <a
          href="https://yoast.com/does-site-speed-influence-seo/"
          target="_blank"
          rel="noopener noreferrer"
        >
          affect SEO negatively
        </a>
        , your page drops in the search results, the traffic falls significantly…
      </p>
      <p>A disaster you’d rather avoid, right?</p>
      <img
        src={img2}
        alt="Page speed illustration"
        className="img-fluid justify-content-center mb-5 mt-3"
        loading="lazy"
        title="Speed is extremly important"
      />
      <p>Here’s what CRO industry experts have to say about it:</p>
      <p className="quote">
        <strong>Page speed</strong> is one of the most important factors when it comes to optimizing
        your website conversion rates. In 2020, it will be more important than ever before. People
        are expecting your site to load as quickly as possible. According to a study by{' '}
        <a
          href="https://www.akamai.com/uk/en/about/news/press/2017-press/akamai-releases-spring-2017-state-of-online-retail-performance-report.jsp"
          target="_blank"
          rel="noopener noreferrer"
        >
          Akamai
        </a>{' '}
        (2017), even a 100ms delay in loading time might decrease your conversion rate by 7%. This
        means that you can have the best landing page in the world, but if it’s loading slowly, it
        probably won’t convert.
        <br />
        <br />
        <strong>Max Benz</strong> – CEO and Co-founder @{' '}
        <a href="https://suitapp.de/" target="_blank" rel="noopener noreferrer">
          suitApp
        </a>{' '}
      </p>
      <p className="quote">
        <i>
          For eCommerce businesses, the CRO technique I would emphasize would be to improve your{' '}
          <strong>site’s page speed</strong>. Firstly, you get the SEO boost from search engines,
          meaning you are more likely to receive an increase in traffic from SERPs which you can
          convert into buyers.
        </i>
        <br />
        <br />
        <i>
          Secondly, those shoppers who do visit your website will get a better impression and will
          be more likely to browse your products and stay longer.
        </i>
        <br />
        <br />
        <i>
          Studies have shown that 42% of people expect a webpage to load in 2 seconds or less and a
          one second delay in that can lead to a{' '}
          <a
            href="https://www.accuagency.com/blog/slow-load-times-can-mean-lost-customers"
            target="_blank"
            rel="noopener noreferrer"
          >
            7% decrease
          </a>{' '}
          in conversion rates.
        </i>
        <br />
        <br />
        <strong>Ben Culpin</strong> – Content Marketer @{' '}
        <a href="https://blog.wakeupdata.com/" target="_blank" rel="noopener noreferrer">
          WakeUpData
        </a>
      </p>
      <p>
        <strong>How to take action:</strong> You can start right away! Here’s some expert advice
        from Ben and Max:
      </p>
      <p className="quote">
        <strong>Ben Culpin:</strong>{' '}
        <i>
          I use Google Analytics to rank the slowest pages load speed times and then Google’s{' '}
          <a
            href="https://developers.google.com/speed/pagespeed/insights/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Page Speed Insights
          </a>{' '}
          tool to get a tailored report of recommendations, including: Reduce redirects, defer
          offscreen images, remove unused CSS, etc. Although completing these optimizations may seem
          a little unimportant to a busy marketer, the stats show us that putting in the hard work
          for this CRO technique consistently yields positive results.
        </i>
      </p>
      <p className="quote">
        <strong>Max Benz:</strong>{' '}
        <i>
          You can get an idea of your current page speed situation by using a page speed test tool
          like{' '}
          <a href="https://gtmetrix.com/" target="_blank" rel="noopener noreferrer">
            GTMetrix
          </a>{' '}
          or{' '}
          <a href="https://www.pingdom.com/" target="_blank" rel="noopener noreferrer">
            Pingdom
          </a>{' '}
          . These free tools will not only help you figure out your current loading times but also
          support you in optimizing it. The analysis will provide you with detailed information on
          how and when different elements (e.g. images, CSS, HTML) are loaded. This way, you can
          research your current loading time in a very detailed way and see what can be improved.
        </i>
      </p>
      <h2>CRO Trend no. 3 – Segmentation is Key:</h2>
      <p>
        Let’s face it: in CRO, there are no one-size-fits-all solutions. The whole idea is to
        experiment with different approaches to find the ones that work best for your target group.
        You can achieve this through personalized content:
      </p>
      <p className="quote">
        <i>
          Authenticity and customer-centrism rule now, and this trend will definitely take center
          stage of all marketers in 2020. We know that nano-influencers and microbrands demonstrate
          a much higher percentage of engagement than business websites and social media accounts
          with 100K+ followers. So, if you have a diverse target audience and market
          products/services to different people, then splitting up your website into its smaller
          versions with <strong>more specific targeting</strong> can be one of your best CRO
          marketing strategies next year.
        </i>
        <br />
        <br />
        <i>
          We can see it on Instagram already: big brands design separate pages for a men’s and
          women’s section of their online clothing stores and split their audience by age,
          geographical location, shopping habits, content engagement, etc. It gives consumers a more
          personalized experience and makes them more likely to buy. As a result, conversion rates
          grow.
        </i>
        <br />
        <br />
        <strong>Lesley Vos</strong> – Content Strategist @{' '}
        <a href="https://bid4papers.com" target="_blank" rel="noopener noreferrer">
          Bid4Papers
        </a>{' '}
      </p>
      <p />
      <p>
        <strong>How to take action:</strong> Start from rethinking your approach to data analysis.
        Instead of trying to draw general conclusions, try looking at your audience from different
        angles. You can read more in our extensive guide to{' '}
        <a
          href="/blog/how-to-use-filters-in-user-research"
          target="_blank"
          rel="noopener noreferrer"
        >
          user segmentation
        </a>
        !
      </p>
      <h2>CRO Trend no. 4 – The Rise of Chatbots:</h2>
      <p>
        We’re at the doorstep of the artificial intelligence era. The use of smart algorithms is
        becoming more prevalent with every month. In the next 5 years, the chatbot market is{' '}
        <a
          href="https://www.statista.com/statistics/656596/worldwide-chatbot-market/"
          target="_blank"
          rel="noopener noreferrer"
        >
          predicted to reach 1.25 billion dollars
        </a>{' '}
        in the US alone.
      </p>
      <img
        src={img3}
        alt="Chatbots in CRO illustration"
        className="img-fluid justify-content-center mb-5 mt-3"
        loading="lazy"
        title="Chatbots are very popular tool"
      />
      <p>
        {' '}
        Are we ready to face this new reality, though? Here are some comments from our experts:
      </p>
      <p className="quote">
        <i>
          Chatbots are great for CRO. Counterintuitively, humans actually open up more when they
          know they’re talking to a bot, as it lowers barriers they normally put up when chatting
          with a person. In order to create engagement with potential customers, you can place the
          chatbot on your price page so that it pops up at a specific time. This way, you can
          improve the time people spend on your site, and increase the chances of converting them
          into paying customers.
        </i>
        <br />
        <br />
        <i>
          If you’re a SaaS company, and you offer a free trial, another useful way to use a chatbot
          is to place it in the web control panel. From here you can provide customers with video or
          text tutorials when they navigate to a specific page.
        </i>
        <br />
        <br />
        <strong>Nikola Baldikov</strong> – Digital Marketing Manager @{' '}
        <a href="https://www.brosix.com/" target="_blank" rel="noopener noreferrer">
          Brosix
        </a>{' '}
      </p>
      <p className="quote">
        <i>
          Chatbots are great for CRO. Counterintuitively, humans actually open up more when they
          know they’re talking to a bot, as it lowers barriers they normally put up when chatting
          with a person. In order to create engagement with potential customers, you can place the
          chatbot on your price page so that it pops up at a specific time. This way, you can
          improve the time people spend on your site, and increase the chances of converting them
          into paying customers.
        </i>
        <br />
        <br />
        <strong>Maja Schaefer</strong> – CEO @{' '}
        <a href="https://chatbotize.com/" target="_blank" rel="noopener noreferrer">
          Chatbotize
        </a>{' '}
      </p>
      <p>
        <strong>How to take action:</strong> Devote some time to find the areas where the bounce
        rate is the highest. Try implementing chatbots in the problematic points of the user journey
        and track your progress – it might be that AI-supported conversations are the missing link
        you were looking for!
      </p>
      <p>
        Chatbots have become a prominent CRO industry trend for another reason, too. They work like
        a charm in customer service. A chatbot can easily answer the most frequently asked
        questions. This increases the chances of conversion and decreases the amount of repetitive
        work – a win-win solution.{' '}
      </p>
      <p>
        Last but not least, if you’re running an online store, you might want to consider another
        idea. Facebook Messenger chatbots can{' '}
        <a
          href="https://chatbotsmagazine.com/the-complete-guide-to-conversational-commerce-e47059293efa"
          target="_blank"
          rel="noopener noreferrer"
        >
          boost ecommerce revenues
        </a>{' '}
        even by 25%. The secret sauce? Use the bot to reach out to a potential client after they
        abandon the cart.{' '}
      </p>
      <h2>CRO Trend no. 5 – The Power of Retargeting:</h2>
      <img
        src={img4}
        alt="Ad retargeting illustration"
        className="img-fluid justify-content-center mb-5 mt-3"
        loading="lazy"
        title="Retargeting is a powerful method of CRO"
      />
      <p>
        Did you know that only{' '}
        <a
          href="https://retargeter.com/what-is-retargeting-and-how-does-it-work/"
          target="_blank"
          rel="noopener noreferrer"
        >
          2% of traffic converts after the first visit
        </a>{' '}
        ? This is one of the reasons why conversion rate optimization is focused on getting the most
        out of your existing traffic. Wondering how to bring your visitors back? Here’s how:
      </p>
      <p className="quote">
        <i>
          One of the effective methods of CRO is retargeting. By adding pixels to links that you
          share, you can track your visitors and reach them through your marketing campaigns. This
          way, you can display more relevant ads to people who have clicked your links before, which
          means they are interested in your offer. In short, you should adjust your social media and
          Google Ads campaigns to your company&apos;s sales funnel to improve your conversion rates.
        </i>
        <br />
        <br />
        <strong>Wojciech Jasnos</strong> – CEO @{' '}
        <a href="https://rocketlink.io/" target="_blank" rel="noopener noreferrer">
          Rocketlink
        </a>{' '}
      </p>
      <p>
        <strong>How to take action:</strong> Take a closer look at retargeting solutions available
        on the market. Tools like{' '}
        <a href="https://rocketlink.io/" target="_blank" rel="noopener noreferrer">
          Rocketlink
        </a>{' '}
        will help you convert your visitors into customers. Sometimes a simple trick can do wonders!
        Sometimes a simple trick can do wonders! For example, you can share links with a retargeting
        pixel in a Facebook group. The best part? It doesn’t even have to be a link to your website!
        You can share anything that can be interesting to your potential customers. There are many
        opportunities to get creative here.
      </p>
      <h3>Key takeaways</h3>
      <p>
        When it comes to the dynamic CRO field, the key is to
        <i>keep your eyes open</i>. Stay on the lookout for new trends and don’t be afraid to try
        new approaches. This is the only way to be one step ahead of your competition!
      </p>
      <p>
        Want to stay up to date with the latest CRO industry trends? Subscribe to our newsletter and
        be the first to know!
      </p>
    </Container>
  );
};

const Wrapped = BlogArticle(Content);

export const frontmatter = {
  title: '5 CRO Trends to Watch in 2020',
  url: '/blog/cro-trends-2020',
  description:
    'Read insights from CRO experts. Get to know the hottest conversion rate optimization trends in 2020 – before your competition does!',
  author,
  img,
  imgSocial: img,
  date: '2019-10-08',
  category: '',
  imgStyle: { borderBottom: '1px solid rgba(0,0,0,0.125)' },
  group: 'Research',
};

export default () => (
  <Wrapped
    title={frontmatter.title}
    category={frontmatter.category}
    description={frontmatter.description}
    date={frontmatter.date}
    author={frontmatter.author}
    url={frontmatter.url}
    img={frontmatter.img}
    imgSocial={process.env.GATSBY_HOST + frontmatter.img}
    group={frontmatter.group}
    descriptionSocial="You can't always sit next to your users and see what exactly they're doing on your website. 
    You don't have to, though!"
  />
);
